import { sBanner, sBannerStep, sPostToEngagementTools, sInputKeyValidationHelper } from '../../shared.min.js';

const homeSecurityImprovements = (function () {
	const widget = document.getElementById('cnfsd-hsi-widget');
	const lockInfoSlideOut = document.getElementById('hsi-slideout');
	const dimmer = document.getElementById('dimmer');
	const downloadButton = document.getElementById('widget__footer__download-button');
	const bannerSteps = [
		widget.getAttribute('data-progress-title-1'),
		widget.getAttribute('data-progress-title-2'),
		widget.getAttribute('data-progress-title-3'),
		widget.getAttribute('data-progress-title-4'),
		widget.getAttribute('data-progress-title-5'),
	];

	const endpointSpan = widget.getAttribute('data-api');
	const apiSubKey = widget.getAttribute('data-api-key') ?? '';
	const apiUrl = endpointSpan ? endpointSpan : 'http://localhost:3000/foundry-ai/home-security-bot';

	let botThreadId = null;

	const formData = {
		propertyLocation: null,
		frontEntranceLock: null,
		hasBackEntrance: null,
		backEntranceLock: null,
		hasWindowLocks: null,
		hasSmartLocks: null,
		hasVideoDoorbell: null,
		hasNonPoliceAlarm: null,
		hasPoliceAlarm: null,
		hasOutdoorCameras: null,
		hasIndoorCameras: null,
		hasMotionLights: null,
		securityInstallationPreference: null,
		budgetAmount: null,
	};

	async function generatePDF() {
		const pdfOptions = {
			margin: 16,
			filename: 'Confused.com_Home_Security_Improvements.pdf',
			image: { type: 'jpeg', quality: 0.98 },
			html2canvas: { scale: 2 },
			jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
		};

		const element = document.getElementById('home-security-improvement-tool__pdf');
		if (element) html2pdf(element, pdfOptions);
	}

	async function init() {
		if (!widget) return;

		sBanner(
			widget,
			bannerSteps,
			'.banner-current-text',
			'.banner-current-progress',
			'.banner__progress-bar__bar',
			1
		);

		// All DOM-related element lookups and event listeners are here.
		const rearEntranceYes = document.getElementById('rear-entrance-yes');
		const rearEntranceNo = document.getElementById('rear-entrance-no');
		const hsiSlideOutCloseButton = document.getElementById('hsi-slideout__close');
		const budgetInput = document.getElementById('budget-value');

		if (rearEntranceYes && rearEntranceNo) {
			rearEntranceYes.addEventListener('change', toggleBackLockFieldset);
			rearEntranceNo.addEventListener('change', toggleBackLockFieldset);
		}

		widget.addEventListener('click', handleClick);

		if (downloadButton) {
			downloadButton.addEventListener('click', async e => {
				e.preventDefault();
				await generatePDF();
			});
		}

		if (budgetInput) {
			sInputKeyValidationHelper(budgetInput, 'numeric');
		}

		if (dimmer) {
			dimmer.addEventListener('click', () => {
				lockInfoSlideOut.classList.add('slide-out--closed');
				dimmer.classList.add('display-none');
			});
		}

		if (hsiSlideOutCloseButton) {
			hsiSlideOutCloseButton.addEventListener('click', showLockInfo);
		}
	}

	/** Handles all button clicks via event delegation. */
	async function handleClick(event) {
		const target = event.target.closest('button, .arrow-link--back');
		if (!target) return;
		event.preventDefault();

		switch (target.id) {
			case 'widget__footer__continue-button':
				await continueToNextStep();
				break;
			case 'widget__footer__back-button':
				goToPreviousStep();
				break;
			case 'hsi-more-info-button-1':
			case 'hsi-more-info-button-2':
				// These buttons share the same action
				showLockInfo(event);
				break;
			case 'hsi-slideout__close':
				showLockInfo(event);
				break;
			case 'widget__footer__restart-button':
				resetForm();
				break;
			default:
				console.error(`Unhandled button click with ID: ${target.id}`);
				break;
		}
	}

	/** Validates the current form step before allowing navigation. */
	function formValidation(stepNumber) {
		const currentForm = document.querySelector(`[data-step="${stepNumber}"]`);
		if (!currentForm) {
			return false;
		}

		let isValid = true;

		// Validation logic for Step 1
		if (stepNumber === '1') {
			const radioButtons = currentForm.querySelectorAll('input[name="location"]');
			const isSelected = [...radioButtons].some(radio => radio.checked);
			const fieldGroup = currentForm.querySelector('.field-group');
			if (!isSelected) {
				fieldGroup.classList.add('field-group--error');
				isValid = false;
			} else {
				fieldGroup.classList.remove('field-group--error');
			}
		}
		// Validation logic for Step 2
		else if (stepNumber === '2') {
			const frontLockFieldset = currentForm.querySelector(
				'[data-testid="hsi-widget__screen-2-question-1-fieldset"]'
			);
			const frontLockSelected = frontLockFieldset.querySelector('input[name="screen2_question2"]:checked');
			if (!frontLockSelected) {
				frontLockFieldset.classList.add('field-group--error');
				isValid = false;
			} else {
				frontLockFieldset.classList.remove('field-group--error');
			}
			const backEntranceFieldset = currentForm.querySelector(
				'[data-testid="hsi-widget__screen-2-question-2-fieldset"]'
			);
			const backEntranceYes = backEntranceFieldset.querySelector(
				'input[name="rear-entrance"][value="yes"]:checked'
			);
			const backEntranceSelected = backEntranceFieldset.querySelector('input[name="rear-entrance"]:checked');
			if (!backEntranceSelected) {
				backEntranceFieldset.classList.add('field-group--error');
				isValid = false;
			} else {
				backEntranceFieldset.classList.remove('field-group--error');
			}
			const backLockFieldset = currentForm.querySelector('#hsi-widget__screen-2-question-2-subquestion-fieldset');
			if (backEntranceYes) {
				const backLockSelected = backLockFieldset.querySelector(
					'input[name="screen2_question2_subquestion"]:checked'
				);
				if (!backLockSelected) {
					backLockFieldset.classList.add('field-group--error');
					isValid = false;
				} else {
					backLockFieldset.classList.remove('field-group--error');
				}
			} else {
				if (backLockFieldset) {
					backLockFieldset.classList.remove('field-group--error');
				}
			}
			const windowLocksFieldset = currentForm.querySelector(
				'[data-testid="hsi-widget__screen-2-question-3-fieldset"]'
			);
			const windowLocksSelected = windowLocksFieldset.querySelector('input[name="window-locks"]:checked');
			if (!windowLocksSelected) {
				windowLocksFieldset.classList.add('field-group--error');
				isValid = false;
			} else {
				windowLocksFieldset.classList.remove('field-group--error');
			}
		}
		// Validation logic for Step 4
		else if (stepNumber === '4') {
			const budgetFieldset = currentForm.querySelector(
				'[data-testid="hsi-widget__screen-4__question-1-fieldset"]'
			);
			const budgetSelected = budgetFieldset.querySelector('input[name="budget"]:checked');
			if (!budgetSelected) {
				budgetFieldset.classList.add('field-group--error');
				isValid = false;
			} else {
				budgetFieldset.classList.remove('field-group--error');
			}
			const budgetValueFieldset = currentForm.querySelector(
				'[data-testid="hsi-widget__screen-4__question-2-fieldset"]'
			);
			const budgetValueInput = budgetValueFieldset.querySelector('input[type="text"]');
			if (budgetValueInput && budgetValueInput.value.trim() === '') {
				budgetValueFieldset.classList.add('field-group--error');
				isValid = false;
			} else {
				budgetValueFieldset.classList.remove('field-group--error');
			}
		}
		return isValid;
	}

	/** Collects data from the current step's form and updates the formData object. */
	function collectDataForCurrentStep(stepNumber) {
		if (stepNumber === '1') {
			const location = document.querySelector('input[name="location"]:checked');
			if (location) {
				formData.propertyLocation = location.value;
			}
		} else if (stepNumber === '2') {
			const frontLock = document.querySelector('input[name="screen2_question2"]:checked');
			if (frontLock) {
				formData.frontEntranceLock = frontLock.value;
			}
			const backEntrance = document.querySelector('input[name="rear-entrance"]:checked');
			if (backEntrance) {
				formData.hasBackEntrance = backEntrance.value;
				if (backEntrance.value === 'yes') {
					const backLock = document.querySelector('input[name="screen2_question2_subquestion"]:checked');
					if (backLock) {
						formData.backEntranceLock = backLock.value;
					}
				} else {
					formData.backEntranceLock = null;
				}
			}
			const windowLocks = document.querySelector('input[name="window-locks"]:checked');
			if (windowLocks) {
				formData.hasWindowLocks = windowLocks.value;
			}
		} else if (stepNumber === '3') {
			const checkboxes = document.querySelectorAll('input[name="security-feature"][type="checkbox"]');
			checkboxes.forEach(checkbox => {
				const dataVal = checkbox.getAttribute('data-checkboxval');
				switch (dataVal) {
					case 'security-feature-smart-locks':
						formData.hasSmartLocks = checkbox.checked;
						break;
					case 'security-feature-video-doorbell':
						formData.hasVideoDoorbell = checkbox.checked;
						break;
					case 'security-feature-indoor-camera(s)':
						formData.hasIndoorCameras = checkbox.checked;
						break;
					case 'security-feature-motion-lights':
						formData.hasMotionLights = checkbox.checked;
						break;
					case 'security-feature-non-police-alerting-alarm-(unmonitored)':
						formData.hasNonPoliceAlarm = checkbox.checked;
						break;
					case 'security-feature-outdoor-camera(s)':
						formData.hasOutdoorCameras = checkbox.checked;
						break;
					case 'security-feature-police-alerting-alarm-(monitored)':
						formData.hasPoliceAlarm = checkbox.checked;
						break;
				}
			});
		} else if (stepNumber === '4') {
			const budgetRadio = document.querySelector('input[name="budget"]:checked');
			if (budgetRadio) {
				formData.securityInstallationPreference = budgetRadio.value;
			}
			const budgetValueInput = document.getElementById('budget-value');
			if (budgetValueInput) {
				formData.budgetAmount = budgetValueInput.value;
			}
		}
	}

	/** Moves the form forward, validating and collecting data. */
	async function continueToNextStep() {
		const currentStepElement = document.querySelector('.widget__screen:not(.display-none)');
		if (!currentStepElement) {
			return;
		}

		const stepNumber = currentStepElement.getAttribute('data-step');
		const isFormValid = formValidation(stepNumber);
		const continueButton = document.getElementById('widget__footer__continue-button');

		if (isFormValid) {
			collectDataForCurrentStep(stepNumber);
			resetScrollAndFocus();

			if (stepNumber === '4') {
				const formattedBotString = formatDataForBot(formData);

				if (continueButton) {
					continueButton.classList.add('btn--loading');
					continueButton.disabled = true;
				}

				try {
					const res = await sPostToEngagementTools(
						{ message: formattedBotString, threadId: botThreadId },
						apiSubKey,
						apiUrl
					);

					if (!res.ok) {
						throw new Error(res.status);
					}

					const data = await res.json();

					// Check for a valid response structure
					if (data.scoring) {
						populateResultsScreen(data);
						goToStep(5); // Go to the normal results screen
					} else {
						// Treat invalid data as an error
						throw new Error('Invalid API response data.');
					}
				} catch (err) {
					console.error('API or Network Error:', err);
					// GO TO THE ERROR PAGE (STEP 6) AND PASS THE ERROR MESSAGE
					goToStep(6, 'There was an error generating your results. Please try again.');
				} finally {
					// Re-enable the button regardless of success or failure
					if (continueButton) {
						continueButton.classList.remove('btn--loading');
						continueButton.disabled = false;
					}
				}
			} else {
				// Normal progression for steps 1-3
				goToStep(parseInt(stepNumber) + 1);
			}
		} else {
			console.error('Validation failed. Cannot proceed to the next step.');
		}
	}

	/** Moves the form back one step. */
	function goToPreviousStep() {
		const currentStepElement = document.querySelector('.widget__screen:not(.display-none)');
		if (!currentStepElement) return;
		const stepNumber = parseInt(currentStepElement.getAttribute('data-step'));
		if (stepNumber > 1) {
			goToStep(stepNumber - 1);
		}
	}

	/** Displays the specified step screen. */
	function goToStep(stepNumber) {
		if (stepNumber < 1 || stepNumber > 6) {
			return;
		}

		const restartButton = document.getElementById('widget__footer__restart-button');
		const continueButton = document.getElementById('widget__footer__continue-button');
		const downloadButton = document.getElementById('widget__footer__download-button');

		const steps = widget.querySelectorAll('[data-step]');
		steps.forEach(step => {
			step.classList.add('display-none');
		});

		const targetStep = widget.querySelector(`[data-step="${stepNumber}"]`);
		if (targetStep) {
			targetStep.classList.remove('display-none');
			const bannerStepForError = stepNumber === 6 ? 5 : stepNumber;
			sBannerStep(bannerStepForError);
			backButtonInitialize(stepNumber);

			if (restartButton && continueButton && downloadButton) {
				if (stepNumber === 5) {
					restartButton.classList.remove('display-none');
					downloadButton.classList.remove('display-none');
					continueButton.classList.add('display-none');
				} else if (stepNumber === 6) {
					restartButton.classList.remove('display-none');
					downloadButton.classList.add('display-none');
					continueButton.classList.add('display-none');
				} else {
					restartButton.classList.add('display-none');
					downloadButton.classList.add('display-none');
					continueButton.classList.remove('display-none');
				}
			} else {
				console.error(`Step ${stepNumber} not found`);
			}
		}
	}

	function populateResultsScreen(data) {
		const scoreSpan = document.querySelector('.score');
		const progressBarSpan = document.querySelector('.results__score-card-bar span'); // Get the progress bar span
		const explanationParagraph = document.querySelector('.results__score-card__description');
		const improvementsContainer = document.querySelector('.results__score-card--items');
		const totalCostValueSpan = document.querySelector('.results__score-card--cost-value');
		const template = document.getElementById('widget__results__card__consideration__template');

		// Get references to the summary elements
		const summaryLocation = document.querySelector('#resultsSummaryLocation .summary-val');
		const summaryLocks = document.querySelector('#resultsSummaryLocks .summary-val');
		const summaryAlarm = document.querySelector('#resultsSummaryAlarm .summary-val');
		const summaryOther = document.querySelector('#resultsSummaryOther .summary-val');

		if (!template) {
			console.error("Template 'widget__results__card__consideration__template' not found.");
			return;
		}

		improvementsContainer.innerHTML = '';
		let totalCost = 0;

		if (data.scoring) {
			// Populate the score
			scoreSpan.textContent = data.scoring.total;

			// Calculate and set the progress bar width
			const scorePercentage = (data.scoring.total / 10) * 100;
			if (progressBarSpan) {
				progressBarSpan.style.width = `${scorePercentage}%`;
			}

			explanationParagraph.textContent = data.scoring.explanation;
		}

		if (data.suggestions && data.suggestions.length > 0) {
			data.suggestions.forEach(suggestion => {
				const clone = template.content.cloneNode(true);
				const nameElement = clone.querySelector('.results__score-card-table__consideration-name');
				const costElement = clone.querySelector('.results__score-card-table__consideration-cost');
				const descriptionElement = clone.querySelector('.results__score-card-table__consideration-description');

				if (nameElement) nameElement.textContent = suggestion.name;
				if (costElement) costElement.textContent = `£${suggestion.cost}`;
				if (descriptionElement) descriptionElement.textContent = suggestion.description;

				improvementsContainer.appendChild(clone);
				totalCost += suggestion.cost;
			});
		}
		totalCostValueSpan.textContent = `£${totalCost}`;

		// Populate and format the summary section
		if (formData.propertyLocation) {
			const locationText = formData.propertyLocation.split('-').pop();
			summaryLocation.textContent = locationText.charAt(0).toUpperCase() + locationText.slice(1);
		}

		const lockParts = [];
		if (formData.frontEntranceLock) {
			lockParts.push(formData.frontEntranceLock);
		}
		if (formData.hasBackEntrance === true && formData.backEntranceLock) {
			lockParts.push(`back door has a ${formData.backEntranceLock}`);
		}
		if (formData.hasWindowLocks === true) {
			lockParts.push(`window locks`);
		}
		summaryLocks.textContent = lockParts.join(', ');

		if (formData.hasPoliceAlarm === true) {
			summaryAlarm.textContent = 'Police-alerting alarm';
		} else if (formData.hasNonPoliceAlarm === true) {
			summaryAlarm.textContent = 'Non-police-alerting alarm';
		} else {
			summaryAlarm.textContent = 'None';
		}

		const otherDevices = [];
		if (formData.hasSmartLocks === true) otherDevices.push('Smart locks');
		if (formData.hasVideoDoorbell === true) otherDevices.push('Video doorbell');
		if (formData.hasOutdoorCameras === true) otherDevices.push('Outdoor cameras');
		if (formData.hasIndoorCameras === true) otherDevices.push('Indoor cameras');
		if (formData.hasMotionLights === true) otherDevices.push('Motion lights');
		summaryOther.textContent = otherDevices.length > 0 ? otherDevices.join(', ') : 'None';

		// Get element by #results__score-card-details_pdf and remove it from dom if exists
		const oldPdfDetails = document.getElementById('results__score-card-details_pdf');
		if (oldPdfDetails) oldPdfDetails.remove();
		// Grab the #results__score-card-details
		const resultsScreenDetails = document.getElementById('results__score-card-details');
		// Clone it
		const resultsScreenDetailsClone = resultsScreenDetails.cloneNode(true);
		// Change the id to #results__score-card-details_pdf
		resultsScreenDetailsClone.id = 'results__score-card-details_pdf';
		// Add open attr to all details elements in the cloned node
		const detailsElements = resultsScreenDetailsClone.querySelectorAll('details');
		detailsElements.forEach(details => details.setAttribute('open', ''));
		// Append clone in the right place
		const pdfContainer = document.querySelector('.home-security-improvement-tool__pdf__container');
		pdfContainer.appendChild(resultsScreenDetailsClone);
	}

	/** Toggles the visibility of the back-entrance lock fieldset. */
	function toggleBackLockFieldset() {
		const backLockFieldset = document.querySelector('#hsi-widget__screen-2-question-2-subquestion-fieldset');
		const rearEntranceYes = document.getElementById('rear-entrance-yes');
		if (rearEntranceYes && backLockFieldset) {
			if (rearEntranceYes.checked) {
				backLockFieldset.classList.remove('display-none');
			} else {
				backLockFieldset.classList.add('display-none');
			}
		}
	}

	function resetForm() {
		// Reset all radio buttons
		const radioButtons = document.querySelectorAll('input[type="radio"]');
		radioButtons.forEach(radio => {
			radio.checked = false;
		});

		const backLockFieldset = document.querySelector('#hsi-widget__screen-2-question-2-subquestion-fieldset');
		if (backLockFieldset) {
			backLockFieldset.classList.add('display-none');
			backLockFieldset.classList.remove('field-group--error'); // Clean up any lingering error class
		}

		// Reset all checkboxes
		const checkboxes = document.querySelectorAll('input[type="checkbox"]');
		checkboxes.forEach(checkbox => {
			checkbox.checked = false;
		});

		// Reset all text fields
		const textFields = document.querySelectorAll('input[type="text"]');
		textFields.forEach(textField => {
			textField.value = '';
		});

		// Clear the formData object and botThreadId
		Object.keys(formData).forEach(key => (formData[key] = null));
		botThreadId = null;
		goToStep(1);
	}

	/** Displays the info slide-out. */
	function showLockInfo(event) {
		event.preventDefault();
		lockInfoSlideOut.classList.toggle('slide-out--closed');
		dimmer.classList.toggle('display-none');
	}

	/** Initializes the back button's visibility and text. */
	function backButtonInitialize(stepNumber) {
		const backButton = document.getElementById('widget__footer__back-button');

		if (stepNumber === 6) {
			if (backButton) backButton.classList.add('display-none');
			return;
		}

		if (backButton) {
			if (stepNumber > 1) {
				const backButtonTextElement = document.getElementById(`screen${stepNumber}_back_button_text`);
				const backButtonTitleSpan = backButton.querySelector('.arrow-link__title');

				if (backButtonTextElement && backButtonTitleSpan) {
					// Set the span's inner text
					backButtonTitleSpan.innerText = backButtonTextElement.innerText;
					backButton.classList.remove('display-none');
				} else {
					backButton.innerText = 'Back';
					backButton.classList.remove('display-none');
				}
			} else {
				backButton.classList.add('display-none');
				backButton.innerText = '';
			}
		}
	}

	/** Resets scroll position and button focus. */
	function resetScrollAndFocus() {
		const continueButton = document.getElementById('widget__footer__continue-button');
		if (widget) {
			// Scrolls the main widget element into view
			widget.scrollIntoView({ behavior: 'smooth' });
		}
		if (continueButton) {
			// Removes focus from the button, preventing the outline from appearing
			continueButton.blur();
		}
	}

	/** Formats the collected data for the bot. */
	function formatDataForBot(data) {
		const lines = [];
		if (data.propertyLocation) lines.push(`- Lives in a ${data.propertyLocation} area`);
		if (data.frontEntranceLock) lines.push(`- Front door has a ${data.frontEntranceLock}`);
		if (data.hasBackEntrance) lines.push(`- Has a back door: ${data.hasBackEntrance}`);
		if (data.hasBackEntrance === 'yes' && data.backEntranceLock)
			lines.push(`- Back door has a ${data.backEntranceLock}`);
		if (data.hasWindowLocks) lines.push(`- Has locks on windows: ${data.hasWindowLocks}`);
		if (data.hasSmartLocks) lines.push(`- Has smart locks: ${data.hasSmartLocks}`);
		if (data.hasVideoDoorbell) lines.push(`- Has a video doorbell: ${data.hasVideoDoorbell}`);
		if (data.hasNonPoliceAlarm) lines.push(`- Has a non-police-alerting alarm: ${data.hasNonPoliceAlarm}`);
		if (data.hasPoliceAlarm) lines.push(`- Has a police-alerting alarm: ${data.hasPoliceAlarm}`);
		if (data.hasOutdoorCameras) lines.push(`- Has outdoor cameras: ${data.hasOutdoorCameras}`);
		if (data.hasIndoorCameras) lines.push(`- Has indoor cameras: ${data.hasIndoorCameras}`);
		if (data.hasMotionLights) lines.push(`- Has motion lights: ${data.hasMotionLights}`);
		if (data.securityInstallationPreference) {
			const option = data.securityInstallationPreference;
			let formattedOption;
			if (option === 'budget-install-it-myself') formattedOption = 'Install it myself';
			else if (option === 'budget-professionally-installed') formattedOption = 'Professionally installed';
			else if (option === 'budget-open-to-both') formattedOption = 'Open to both';
			lines.push(`- Installation option: ${formattedOption}`);
		}
		if (data.budgetAmount) lines.push(`- Budget: £${data.budgetAmount}`);
		return lines.join('\n');
	}

	// **3. Public API**
	return {
		init: init,
	};
})();

document.addEventListener('DOMContentLoaded', function () {
	homeSecurityImprovements.init();
});
